// ############################################################
// Authentication Utilities
// ############################################################

import { Auth } from 'aws-amplify';

/**
 * Gets the current authenticated user's JWT tokens
 * @returns {Promise<Object>} Object containing accessToken, idToken, and refreshToken
 */
export const getUserTokens = async () => {
  try {
    const session = await Auth.currentSession();
    return {
      accessToken: session.getAccessToken().getJwtToken(),
      idToken: session.getIdToken().getJwtToken(),
      refreshToken: session.getRefreshToken().getToken(),
      // Expiration times
      accessTokenExpiration: session.getAccessToken().getExpiration(),
      idTokenExpiration: session.getIdToken().getExpiration(),
    };
  } catch (error) {
    console.error('Error getting user tokens:', error);
    return null;
  }
};

/**
 * Gets the current user's bearer token (access token) for authorization headers
 * @returns {Promise<string|null>} The bearer token or null if not available
 */
export const getBearerToken = async () => {
  try {
    const session = await Auth.currentSession();

    // Check if token is about to expire (within next 5 minutes)
    const accessToken = session.getAccessToken();
    const expirationTime = accessToken.getExpiration() * 1000; // Convert to milliseconds
    const currentTime = Date.now();
    const timeUntilExpiration = expirationTime - currentTime;

    // If token is about to expire, try to refresh it
    if (timeUntilExpiration < 300000) {
      // less than 5 minutes
      try {
        // Refresh the session
        await Auth.currentAuthenticatedUser({ bypassCache: true });
        const newSession = await Auth.currentSession();
        return newSession.getAccessToken().getJwtToken();
      } catch (refreshError) {
        console.error('Failed to refresh token:', refreshError);
        // Fall back to the original token if refresh fails
      }
    }

    return accessToken.getJwtToken();
  } catch (error) {
    console.error('Error getting bearer token:', error);

    // Add more detailed error information
    if (error.code === 'NoUserPoolError') {
      console.error(
        'No user pool configured. Make sure AWS Amplify is properly set up.'
      );
    } else if (error.code === 'NotAuthorizedException') {
      console.error('User is not authorized. Session may have expired.');
    }

    return null;
  }
};

/**
 * Creates an authorization header with the current user's bearer token
 * @returns {Promise<Object|null>} Headers object with Authorization or null if not available
 */
export const getAuthorizationHeader = async () => {
  const token = await getBearerToken();
  if (!token) return null;

  return {
    Authorization: `Bearer ${token}`,
  };
};

/**
 * Gets the current authenticated user information
 * @returns {Promise<Object|null>} Cognito user information or null if not available
 */
export const getCurrentUser = async () => {
  try {
    return await Auth.currentAuthenticatedUser();
  } catch (error) {
    console.error('Error getting current user:', error);
    return null;
  }
};

/**
 * Gets the AWS Cognito credentials for the current user
 * @returns {Promise<Object|null>} AWS credentials or null if not available
 */
export const getCognitoCredentials = async () => {
  try {
    return await Auth.currentCredentials();
  } catch (error) {
    console.error('Error getting cognito credentials:', error);
    return null;
  }
};

/**
 * Gets the AWS Cognito identity ID for the current user
 * @returns {Promise<string|null>} The identity ID or null if not available
 */
export const getCognitoIdentityId = async () => {
  try {
    const credentials = await Auth.currentCredentials();
    return credentials.identityId;
  } catch (error) {
    console.error('Error getting cognito identity ID:', error);
    return null;
  }
};

/**
 * Gets the current user's ID token for authorization headers
 * Sometimes API Gateway Authorizers are configured to use ID tokens rather than access tokens
 * @returns {Promise<string|null>} The ID token or null if not available
 */
export const getIdToken = async () => {
  try {
    const session = await Auth.currentSession();
    return session.getIdToken().getJwtToken();
  } catch (error) {
    console.error('Error getting ID token:', error);
    return null;
  }
};
